import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Eraser, PenTool, Type } from 'lucide-react';

const SignatureCanvas = ({ 
  onSignatureChange, 
  initialSignature = null,
  width = 500,
  height = 200,
  disabled = false,
  className = ""
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mode, setMode] = useState('draw'); // 'draw' or 'type'
  const [typedName, setTypedName] = useState('');
  const [hasSignature, setHasSignature] = useState(false);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1e293b'; // slate-800 background
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Load initial signature if provided
    if (initialSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = initialSignature;
    }
  }, [initialSignature]);

  // Get mouse/touch position relative to canvas
  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (disabled || mode !== 'draw') return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPosition(e);
    
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || disabled || mode !== 'draw') return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPosition(e);
    
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      emitSignature();
    }
  };

  const emitSignature = () => {
    const canvas = canvasRef.current;
    if (canvas && hasSignature) {
      const dataUrl = canvas.toDataURL('image/png');
      onSignatureChange?.(dataUrl, mode === 'type' ? typedName : null);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setTypedName('');
    onSignatureChange?.(null, null);
  };

  const drawTypedSignature = (name) => {
    if (!name) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw signature line
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, canvas.height - 40);
    ctx.lineTo(canvas.width - 20, canvas.height - 40);
    ctx.stroke();
    
    // Draw typed name in cursive style
    ctx.fillStyle = '#ffffff';
    ctx.font = 'italic 48px "Dancing Script", cursive';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(name, canvas.width / 2, canvas.height - 50);
    
    setHasSignature(true);
    
    // Emit the signature
    const dataUrl = canvas.toDataURL('image/png');
    onSignatureChange?.(dataUrl, name);
  };

  const handleTypedNameChange = (e) => {
    const name = e.target.value;
    setTypedName(name);
    if (name) {
      drawTypedSignature(name);
    } else {
      clearCanvas();
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={mode === 'draw' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setMode('draw'); clearCanvas(); }}
          disabled={disabled}
          className={mode === 'draw' ? 'bg-orange-500 hover:bg-orange-600' : 'border-slate-600 text-slate-300'}
        >
          <PenTool size={16} className="mr-2" />
          Draw
        </Button>
        <Button
          type="button"
          variant={mode === 'type' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setMode('type'); clearCanvas(); }}
          disabled={disabled}
          className={mode === 'type' ? 'bg-orange-500 hover:bg-orange-600' : 'border-slate-600 text-slate-300'}
        >
          <Type size={16} className="mr-2" />
          Type
        </Button>
        <div className="flex-1" />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearCanvas}
          disabled={disabled}
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
        >
          <Eraser size={16} className="mr-2" />
          Clear
        </Button>
      </div>

      {/* Canvas Container */}
      <div className="relative rounded-lg overflow-hidden border-2 border-dashed border-slate-600 bg-slate-800">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className={`w-full touch-none ${mode === 'draw' && !disabled ? 'cursor-crosshair' : ''}`}
          style={{ maxHeight: `${height}px` }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        
        {/* Overlay instructions */}
        {!hasSignature && mode === 'draw' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-slate-500 text-lg">Draw your signature here</p>
          </div>
        )}
        
        {/* Signature line indicator */}
        <div className="absolute bottom-10 left-5 right-5 border-b border-slate-600 pointer-events-none" />
        <div className="absolute bottom-3 left-5 text-slate-500 text-xs pointer-events-none">
          ✕ Sign Here
        </div>
      </div>

      {/* Type signature input */}
      {mode === 'type' && (
        <input
          type="text"
          value={typedName}
          onChange={handleTypedNameChange}
          placeholder="Type your full legal name"
          disabled={disabled}
          className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      )}
    </div>
  );
};

export default SignatureCanvas;
